/*
An image block's `src` answers to two masters, and this module is the referee.

MASTER ONE is the editor picking a picture. That is what the media library is
for, and it is what the product asked for: choose or upload an asset, get a URL.

MASTER TWO is the campaign supplying the picture at send time. The 26 PRO
networking templates have a hero whose src is literally `{{{imageUrl}}}` — the
sending code fills it in per recipient. If the inspector only ever offered a
picker, opening one of those and touching the image would bake in a fixed URL
and break every send from then on. So the URL stays typeable, and the control
has to be able to tell the two cases apart to know what to DRAW: a thumbnail
for a real URL, and the variable spelled out for a Handlebars one — never an
<img> pointed at `{{{imageUrl}}}`, which resolves to nothing and renders as a
broken-image icon, i.e. as an error the editor did not make.

That "which of the two is this?" question is the whole of `imageSrcKind`, and
`imagePatchFromSelection` is the other half: what the media library hands back,
turned into the props to write. It is deliberately the one that decides whether
alt is included, because "do not clobber an alt the user already wrote" is a
rule, not a rendering detail, and a rule belongs where it can be tested.

ps-admin has no Jest, no Vitest and no RTL — a component is untestable here by
construction — so both live here as CommonJS and run under plain
`node src/utils/emailImageSrc.test.js`.

Nothing here mutates its input. Apollo freezes query results in this admin
(`__DEV__` is undefined in the bundles), so the block and the asset objects
reaching these functions may be frozen, and writing through to one would throw
in production and nowhere else.
*/

// Anything of the form {{…}} anywhere in the string. `{{{x}}}` matches too: the
// lazy body stops at the first `}}`, which is all a yes/no question needs.
//
// A src that merely CONTAINS an expression — `https://cdn/{{slug}}.png` — is
// just as unresolvable in a preview as one that is nothing else, so containment
// is the right test for the drawing decision.
const HANDLEBARS_ANYWHERE = /\{\{[\s\S]*?\}\}/;

// The whole string is one expression, double- or triple-stache. Not what the
// preview branches on, but it is what tells "this src IS a variable" from "this
// src is a URL with a variable in the middle of it", which is worth being able
// to say.
const WHOLE_HANDLEBARS = /^\{\{\{?\s*[^{}]+\s*\}?\}\}$/;

const asString = value => (typeof value === 'string' ? value : '');

const containsHandlebars = src => HANDLEBARS_ANYWHERE.test(asString(src));

const isHandlebarsExpression = src => WHOLE_HANDLEBARS.test(asString(src).trim());

/**
 * Which of the three things an image src is, from the point of view of anything
 * that wants to DRAW it.
 *
 *   'empty'    nothing set yet
 *   'variable' holds Handlebars, so it cannot be resolved here and must not be
 *              put in an <img> — show the expression itself instead
 *   'url'      an ordinary address, previewable
 *
 * A protocol-relative `//host/x.png` is a URL: it resolves fine in the browser
 * that draws the preview, whatever a mail client later makes of it.
 */
const imageSrcKind = (src) => {
  const trimmed = asString(src).trim();
  if (!trimmed) return 'empty';
  if (containsHandlebars(trimmed)) return 'variable';
  return 'url';
};

// The media library stores a description as a localised object; some older
// assets carry a bare string. Either way alt text is one line, in one language.
const flattenDescription = (description, language) => {
  if (typeof description === 'string') return description.trim();
  if (!description || typeof description !== 'object') return '';

  const preferred = asString(description[language || 'en']).trim();
  if (preferred) return preferred;

  const fallback = Object.keys(description)
    .map(key => asString(description[key]).trim())
    .filter(Boolean);
  return fallback.length ? fallback[0] : '';
};

/**
 * The props to write when someone picks an asset in the media library.
 *
 * `selected` is what SingleObjUploaderWithSelector passes to its selectCallback:
 * an ARRAY, of which only the first entry matters (it is a single-select). The
 * array is accepted here rather than destructured at the call site so that the
 * whole contract with the media library sits in the tested layer.
 *
 * Returns null when there is nothing usable — the caller then does nothing at
 * all rather than writing an empty src over a good one.
 *
 * `alt` is included ONLY when the block has none. An editor who has written alt
 * text has said something the asset's own description does not know, and
 * swapping the picture is not a reason to throw it away.
 */
const imagePatchFromSelection = (selected, currentAlt, language) => {
  const asset = Array.isArray(selected) ? selected[0] : selected;
  if (!asset || typeof asset !== 'object') return null;

  const src = asString(asset.url).trim();
  if (!src) return null;

  const patch = { src };

  if (!asString(currentAlt).trim()) {
    const description = flattenDescription(asset.description, language);
    if (description) patch.alt = description;
  }

  return patch;
};


/**
 * Should this url be rewritten to the image CDN before being stored in an email?
 *
 * Only an ORIGIN url should. The media library hands those back, and they do not
 * reliably load in a browser — the bug this exists for was an asset picked from
 * the library drawing a broken image in the canvas while the inspector thumbnail
 * beside it, which goes through imageModifier, rendered perfectly.
 *
 * Everything else is left alone, and the important case is a url ALREADY on the
 * CDN: imageModifier is not idempotent, so rewriting one a second time produces
 * assets-img.img.primaverasound.com/fit-in/1200x1200/600x600/… — a host that does
 * not exist and a doubled size segment. Picking an image twice is enough to hit
 * it.
 *
 * A Handlebars src is not a url at all and must never be touched: the campaign
 * supplies that image at send time.
 */
const needsCdnRewrite = (url) => {
  const value = asString(url).trim();
  if (!value) return false;
  if (imageSrcKind(value) !== 'url') return false;
  if (value.includes('assets-img.')) return false;
  return value.includes('assets.primaverasound.com');
};

module.exports = {
  needsCdnRewrite,
  containsHandlebars,
  isHandlebarsExpression,
  imageSrcKind,
  flattenDescription,
  imagePatchFromSelection,
};
